import { useEffect, useMemo, useRef, useState } from "react";
import type { ColumnFilter } from "../utils/apply-filters";
import type { ColumnKind } from "../utils/derive-column-kind";

const CheckIcon = ({ size = 12 }: { size?: number }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 12 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden
	>
		<path
			d="M2.5 6L5 8.5L9.5 4"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const SearchIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 16 16"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden
	>
		<circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
		<path
			d="M10.5 10.5L14 14"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	</svg>
);

const SortRadioCard = ({
	label,
	checked,
	onClick,
}: {
	label: string;
	checked: boolean;
	onClick: () => void;
}) => (
	<button
		type="button"
		role="radio"
		aria-checked={checked}
		data-checked={checked ? "" : undefined}
		onClick={onClick}
		className="tw-flex tw-flex-1 tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-white tw-border-0 tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-100 tw-cursor-pointer data-[checked]:tw-bg-[#f2f0ff] data-[checked]:tw-outline-[#610cff]"
	>
		<span
			data-checked={checked ? "" : undefined}
			className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-100 tw-bg-white tw-flex-shrink-0 data-[checked]:tw-outline-[#610cff]"
			aria-hidden
		>
			{checked && (
				<span className="tw-h-2.5 tw-w-2.5 tw-rounded-full tw-bg-[#610cff]" />
			)}
		</span>
		<span className="tw-font-medium tw-text-sm tw-leading-5 tw-text-blue-base">
			{label}
		</span>
	</button>
);

export const SortFilterSheet = ({
	sortEnabled,
	filterEnabled,
	columnKind,
	values,
	initialSort,
	initialFilter,
	onApply,
	onReset,
	onClose,
}: {
	sortEnabled: boolean;
	filterEnabled: boolean;
	columnKind: ColumnKind;
	values: string[];
	initialSort: "asc" | "desc" | null;
	initialFilter: ColumnFilter | null;
	onApply: (sort: "asc" | "desc" | null, filter: ColumnFilter | null) => void;
	onReset: () => void;
	onClose: () => void;
}) => {
	const [sortDir, setSortDir] = useState<"asc" | "desc" | null>(initialSort);
	const [pendingValues, setPendingValues] = useState<Set<string>>(() =>
		initialFilter && initialFilter.kind === "values"
			? new Set(initialFilter.selected)
			: new Set<string>(values),
	);
	const [minStr, setMinStr] = useState<string>(() =>
		initialFilter &&
		initialFilter.kind === "range" &&
		initialFilter.min !== null
			? String(initialFilter.min)
			: "",
	);
	const [maxStr, setMaxStr] = useState<string>(() =>
		initialFilter &&
		initialFilter.kind === "range" &&
		initialFilter.max !== null
			? String(initialFilter.max)
			: "",
	);
	const [search, setSearch] = useState("");
	const sheetRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		const previouslyFocused = document.activeElement as HTMLElement | null;
		const sheet = sheetRef.current;
		const focusables = () =>
			sheet
				? Array.from(
						sheet.querySelectorAll<HTMLElement>(
							"input, button, [tabindex]:not([tabindex='-1'])",
						),
					)
				: [];
		(focusables()[0] ?? sheet)?.focus();
		const handleMouse = (e: MouseEvent) => {
			const target = e.target as HTMLElement | null;
			if (target?.closest?.("[data-filter-trigger]")) return;
			if (!sheet?.contains(target)) onClose();
		};
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onClose();
				return;
			}
			if (e.key !== "Tab") return;
			const items = focusables();
			if (items.length === 0) return;
			const first = items[0];
			const last = items[items.length - 1];
			const active = document.activeElement;
			if (e.shiftKey) {
				if (active === first || !sheet?.contains(active)) {
					e.preventDefault();
					last.focus();
				}
			} else if (active === last) {
				e.preventDefault();
				first.focus();
			}
		};
		document.addEventListener("mousedown", handleMouse);
		document.addEventListener("keydown", handleKey);
		return () => {
			document.removeEventListener("mousedown", handleMouse);
			document.removeEventListener("keydown", handleKey);
			previouslyFocused?.focus?.();
		};
	}, [onClose]);

	const visibleValues = useMemo(() => {
		if (columnKind !== "text") return values;
		const q = search.trim().toLowerCase();
		if (!q) return values;
		return values.filter((v) => v.toLowerCase().includes(q));
	}, [columnKind, values, search]);

	const allVisibleChecked =
		visibleValues.length > 0 &&
		visibleValues.every((v) => pendingValues.has(v));

	const handleSelectAll = () => {
		const next = new Set(pendingValues);
		if (allVisibleChecked) {
			for (const v of visibleValues) next.delete(v);
		} else {
			for (const v of visibleValues) next.add(v);
		}
		setPendingValues(next);
	};

	const handleToggle = (v: string) => {
		const next = new Set(pendingValues);
		if (next.has(v)) next.delete(v);
		else next.add(v);
		setPendingValues(next);
	};

	const handleApply = () => {
		let filter: ColumnFilter | null = null;
		if (filterEnabled) {
			if (columnKind === "number") {
				const min = minStr.trim() === "" ? null : Number(minStr);
				const max = maxStr.trim() === "" ? null : Number(maxStr);
				const safeMin = min !== null && Number.isFinite(min) ? min : null;
				const safeMax = max !== null && Number.isFinite(max) ? max : null;
				filter =
					safeMin === null && safeMax === null
						? null
						: { kind: "range", min: safeMin, max: safeMax };
			} else if (
				pendingValues.size !== values.length &&
				pendingValues.size > 0
			) {
				filter = { kind: "values", selected: pendingValues };
			}
		}
		onApply(sortEnabled ? sortDir : null, filter);
	};

	return (
		<div
			ref={sheetRef}
			className="table-cards-mobile-filter-sheet tw-flex tw-w-full tw-flex-col tw-gap-4 tw-p-4"
			role="dialog"
			aria-modal="true"
			aria-label="Sort and filter rows"
			tabIndex={-1}
		>
			{sortEnabled && (
				<div className="tw-flex tw-flex-col tw-gap-2">
					<span className="tw-not-italic tw-font-medium tw-text-sm tw-leading-5 tw-text-blue-base">
						Sort
					</span>
					<div className="tw-flex tw-gap-2">
						<SortRadioCard
							label="Sort A-Z"
							checked={sortDir === "asc"}
							onClick={() => setSortDir(sortDir === "asc" ? null : "asc")}
						/>
						<SortRadioCard
							label="Sort Z-A"
							checked={sortDir === "desc"}
							onClick={() => setSortDir(sortDir === "desc" ? null : "desc")}
						/>
					</div>
				</div>
			)}
			{filterEnabled && (
				<>
					<div className="tw-flex tw-flex-col tw-gap-2 tw-py-1">
						<div className="tw-flex tw-items-center tw-justify-between tw-h-8">
							<span className="tw-not-italic tw-font-medium tw-text-sm tw-leading-5 tw-text-blue-base">
								Filter
							</span>
							{columnKind === "text" && (
								<button
									type="button"
									onClick={handleSelectAll}
									className="tw-flex tw-h-9 tw-items-center tw-justify-center tw-gap-0.5 tw-px-3 tw-rounded-lg tw-bg-[transparent] tw-border-0 tw-cursor-pointer tw-font-medium tw-text-sm tw-leading-5 tw-text-blue-base"
								>
									<span className="tw-px-1">Select all</span>
								</button>
							)}
						</div>
						{columnKind === "text" && (
							<label className="tw-flex tw-h-9 tw-items-center tw-gap-2.5 tw-px-3 tw-py-1 tw-bg-white tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-100 tw-rounded-lg focus-within:tw-outline-[#610cff]">
								<span className="tw-text-blue-500 tw-flex">
									<SearchIcon />
								</span>
								<input
									type="text"
									placeholder="Search"
									value={search}
									onChange={(e) => setSearch(e.target.value)}
									className="tw-flex-1 tw-border-0 tw-bg-[transparent] tw-outline-none tw-font-normal tw-text-sm tw-leading-5 tw-text-blue-base placeholder:tw-text-blue-500"
								/>
							</label>
						)}
					</div>
					{columnKind === "number" ? (
						<div className="tw-flex tw-gap-2">
							<label className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-min-w-0">
								<span className="tw-font-medium tw-text-xs tw-leading-4 tw-text-blue-base">
									Min
								</span>
								<input
									type="number"
									inputMode="decimal"
									placeholder="—"
									value={minStr}
									onChange={(e) => setMinStr(e.target.value)}
									className="tw-h-9 tw-w-full tw-px-3 tw-py-1 tw-bg-white tw-border-0 tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-100 tw-rounded-lg tw-font-normal tw-text-sm tw-leading-5 tw-text-blue-base focus:tw-outline-[#610cff]"
									aria-label="Minimum value"
								/>
							</label>
							<label className="tw-flex tw-flex-1 tw-flex-col tw-gap-1 tw-min-w-0">
								<span className="tw-font-medium tw-text-xs tw-leading-4 tw-text-blue-base">
									Max
								</span>
								<input
									type="number"
									inputMode="decimal"
									placeholder="—"
									value={maxStr}
									onChange={(e) => setMaxStr(e.target.value)}
									className="tw-h-9 tw-w-full tw-px-3 tw-py-1 tw-bg-white tw-border-0 tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue-100 tw-rounded-lg tw-font-normal tw-text-sm tw-leading-5 tw-text-blue-base focus:tw-outline-[#610cff]"
									aria-label="Maximum value"
								/>
							</label>
						</div>
					) : (
						<div className="tw-flex tw-flex-col tw-gap-2 tw-max-h-[40vh] tw-overflow-y-auto">
							{visibleValues.map((v) => {
								const checked = pendingValues.has(v);
								return (
									<button
										key={v}
										type="button"
										role="checkbox"
										aria-checked={checked}
										onClick={() => handleToggle(v)}
										data-checked={checked ? "" : undefined}
										className="tw-flex tw-items-center tw-gap-3 tw-p-3 tw-rounded-lg tw-bg-white tw-border tw-border-[rgba(18,3,62,0.12)] tw-cursor-pointer tw-w-full data-[checked]:tw-bg-[#f2f0ff] data-[checked]:tw-border-[#610cff]"
									>
										<span
											className="tw-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded tw-border tw-border-[rgba(18,3,62,0.12)] tw-bg-white data-[checked]:tw-bg-[#610cff] data-[checked]:tw-text-white"
											data-checked={checked ? "" : undefined}
											aria-hidden
										>
											{checked && <CheckIcon size={12} />}
										</span>
										<span className="tw-flex-1 tw-font-medium tw-text-sm tw-leading-5 tw-text-blue-base tw-text-left">
											{v === "" ? "(empty)" : v}
										</span>
									</button>
								);
							})}
						</div>
					)}
				</>
			)}
			<div className="tw-flex tw-items-start tw-gap-2">
				<button
					type="button"
					onClick={onReset}
					className="tw-flex tw-flex-1 tw-h-10 tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-rounded-lg tw-bg-[transparent] tw-border-0 tw-cursor-pointer tw-font-medium tw-text-base tw-leading-6 tw-text-blue-base"
				>
					Reset
				</button>
				<button
					type="button"
					onClick={handleApply}
					className="tw-flex tw-flex-1 tw-h-10 tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-rounded-lg tw-bg-[#610cff] tw-border-0 tw-cursor-pointer tw-font-medium tw-text-base tw-leading-6 tw-text-[#f2f0ff]"
				>
					Apply
				</button>
			</div>
		</div>
	);
};
